import React, { useState } from 'react';
import { X, ShieldAlert, Check } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import type { UnmetGate } from './types';

interface ApproveOverrideModalProps {
  uid: string;
  applicantName?: string;
  /** Unmet, non-overridden gates from the server's 409 UNMET_GATES payload. */
  unmetGates: UnmetGate[];
  /** Decision notes typed in the drawer, carried into the approval. */
  notes?: string;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Optimistic display hooks (drawer responsiveness fix): fired the
   * moment the admin confirms, before the request resolves, so the
   * drawer shows the next stage immediately; `onMoveFailed` rolls
   * that display back when the server refuses.
   */
  onOptimisticMove?: () => void;
  onMoveFailed?: () => void;
}

/**
 * Approve-with-override (2026-10-12) — the guided flow when an admin clicks
 * Approve at "Submitted for Review" but pre-vetting gates are still unmet.
 *
 * The server blocked the bare approval with 409 UNMET_GATES and listed the
 * gates. This dialog lists them again, lets the admin multi-select which to
 * override, and requires an INDIVIDUAL reason per gate. Approval only
 * proceeds when every unmet gate is either left (blocking) or overridden —
 * overriding one gate never waives another.
 */
export const ApproveOverrideModal: React.FC<ApproveOverrideModalProps> = ({
  uid,
  applicantName,
  unmetGates,
  notes,
  onClose,
  onSuccess,
  onOptimisticMove,
  onMoveFailed,
}) => {
  const [selected, setSelected] = useState<string[]>(() => unmetGates.map((g) => g.key));
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const toggleGate = (key: string) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const uncovered = unmetGates.filter((g) => !selected.includes(g.key));
  const allCovered = uncovered.length === 0 && selected.length > 0;
  const reasonsValid = selected.every((k) => (reasons[k] || '').trim().length >= 20);
  const canSubmit = allCovered && reasonsValid && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allCovered) {
      notifyToast('error', 'Every unmet gate must be overridden before approval can proceed — overriding one gate does not waive the others.');
      return;
    }
    if (!reasonsValid) {
      notifyToast('error', 'Every overridden gate needs its own reason of at least 20 characters.');
      return;
    }
    setSubmitting(true);
    // Confirmed: the drawer shows the next stage from this moment; a
    // refusal below rolls that display back.
    onOptimisticMove?.();
    try {
      const res = await safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/pre-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision: 'approve',
          notes: notes?.trim() || undefined,
          gateOverrides: selected.map((k) => ({ gateKey: k, reason: (reasons[k] || '').trim() })),
        }),
      });
      if (res?.success) {
        notifyToast('success', 'Approved — gate overrides recorded');
        onSuccess();
        onClose();
      } else {
        onMoveFailed?.();
      }
    } catch (err: any) {
      onMoveFailed?.();
      // A fresh 409 means the gate set changed underneath us (or a gate was
      // missed) — surface the server's message and keep the dialog open.
      notifyToast('error', err.message || 'Approval failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9000] bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in font-sans">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 space-y-4 border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2 text-purple-900 font-extrabold text-sm">
            <ShieldAlert className="h-5 w-5 text-purple-600" />
            <span>Approve with gate overrides</span>
          </div>
          <button aria-label="Close" title="Close"
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 text-xs text-amber-950 space-y-1.5">
          <p className="font-extrabold">
            {applicantName ? <span className="underline">{applicantName}</span> : 'This applicant'} cannot be
            approved yet — {unmetGates.length} pre-vetting gate{unmetGates.length === 1 ? ' is' : 's are'} unmet.
          </p>
          <p className="text-[11px] font-medium text-amber-800">
            Approval is blocked until every unmet gate is met or individually overridden below, each with
            its own reason. Overriding one gate never waives another. Overrides are permanently logged and
            shown on the gate panel and timeline.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div className="space-y-2">
            {unmetGates.map((g) => {
              const isSelected = selected.includes(g.key);
              const value = reasons[g.key] || '';
              const valid = value.trim().length >= 20;
              return (
                <div key={g.key} className={`rounded-xl border px-3 py-2.5 ${isSelected ? 'border-purple-300 bg-purple-50/50' : 'border-slate-200 bg-white'}`}>
                  <button
                    type="button"
                    onClick={() => toggleGate(g.key)}
                    aria-pressed={isSelected}
                    className="w-full text-left text-xs font-semibold text-slate-800 cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <span className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 ${isSelected ? 'border-purple-600 bg-purple-600' : 'border-slate-300 bg-white'}`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </span>
                      {g.label}
                      <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">unmet</span>
                    </span>
                  </button>
                  {g.detail && <p className="mt-1 pl-6 text-[11px] text-slate-500">{g.detail}</p>}
                  {g.aiVerdict && (
                    <p className="mt-1 pl-6 text-[11px] text-violet-700">
                      <span className="font-bold uppercase">AI: {g.aiVerdict.verdict}</span> — {g.aiVerdict.reason}
                      {g.aiVerdict.verdict === 'undetermined' && g.aiVerdict.missingDetail
                        ? ` Missing: ${g.aiVerdict.missingDetail}`
                        : ''}
                    </p>
                  )}
                  {isSelected && (
                    <div className="mt-2 pl-6">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold text-purple-900">Override reason for this gate *</span>
                        <span className={`text-[10px] font-mono font-bold ${valid ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {value.trim().length}/20
                        </span>
                      </div>
                      <textarea
                        rows={3}
                        value={value}
                        onChange={(e) => setReasons((prev) => ({ ...prev, [g.key]: e.target.value }))}
                        placeholder={`Why is "${g.label}" being bypassed for this applicant?`}
                        aria-label={`Override reason for ${g.label}`}
                        className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-xs font-medium text-slate-800 outline-none focus:border-purple-500"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {uncovered.length > 0 && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] font-bold text-amber-800">
              Not overridden: {uncovered.map((g) => g.label).join(', ')} — approval stays blocked until
              {uncovered.length === 1 ? ' this gate is' : ' these gates are'} selected with a reason.
            </p>
          )}

          <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-5 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white font-extrabold rounded-xl shadow-xs cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              <span>{submitting ? 'Approving...' : 'Approve with overrides'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
